import _ from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, ReactNode, useMemo, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncMoreStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemCreate} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCreate';
import {DropdownItemIcon} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {DropdownItemLoader} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemLoader';
import {SearchDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {doesInboxMatchQuery} from '../../../../helpers/modelHelpers';
import {DropdownItemInboxDot} from '../dropdownItemInboxDot';
import {TypedRowValue} from '../source/dropdownSource';

/*
 * Constants.
 */

enum SearchInboxDropdownItemsEnum {
  CREATE_INBOX_HEADER = 'create_inbox_header',
  CREATE_INBOX = 'create_inbox',
  INBOX_HEADER = 'inbox_header',
  INBOX = 'inbox',
  LOADER = 'loader',
  NONE = 'none',
}

/*
 * Props.
 */

export interface SearchInboxDropdownProps extends DropdownProps {
  inboxes: ReadonlyArray<InboxModel>;
  selectedInboxIds: ReadonlyArray<number>;
  shouldShowDeleteOption?: boolean;
  onChange: (inbox: InboxModel) => void;
  onCreateInbox?: () => void;
  onDelete?: () => void;

  // Async dropdown props.
  onLoadMore?: () => void;
  asyncMoreStatus?: AsyncMoreStatusesEnum;
  query?: string;
  onSetQuery?: (query: string) => void;

  isMultiSelect?: boolean;

  headerTitle?: ReactNode;
  headerRightIcon?: ReactNode;
  footer?: ReactNode;
}

type SearchInboxDropdownRowTypedValue =
  | TypedRowValue<SearchInboxDropdownItemsEnum.CREATE_INBOX_HEADER, undefined>
  | TypedRowValue<SearchInboxDropdownItemsEnum.CREATE_INBOX, undefined>
  | TypedRowValue<SearchInboxDropdownItemsEnum.INBOX_HEADER, undefined>
  | TypedRowValue<SearchInboxDropdownItemsEnum.INBOX, InboxModel>
  | TypedRowValue<SearchInboxDropdownItemsEnum.LOADER, undefined>
  | TypedRowValue<SearchInboxDropdownItemsEnum.NONE, undefined>;

/*
 * Component.
 */

export const SearchInboxDropdown: FC<SearchInboxDropdownProps> = (props) => {
  const {
    inboxes,
    selectedInboxIds,
    shouldShowDeleteOption = false,
    onChange,
    onDelete,
    onCreateInbox,
    onLoadMore,
    asyncMoreStatus,
    query: asyncQuery,
    onSetQuery: onSetAsyncQuery,
    isMultiSelect,
    headerTitle,
    headerRightIcon,
    footer,
    ...dropdownProps
  } = props;
  const [internalQuery, setInternalQuery] = useState('');

  const query = asyncQuery ?? internalQuery;
  const setQuery = onSetAsyncQuery ?? setInternalQuery;

  const data = useMemo(
    () => buildDropdownItems(inboxes, shouldShowDeleteOption, Boolean(onCreateInbox), asyncMoreStatus),
    [asyncMoreStatus, inboxes, onCreateInbox, shouldShowDeleteOption],
  );

  // If this is an async component, the filter is done in the parent component.
  const filteredData = useMemo(() => filterRows(data, internalQuery), [data, internalQuery]);

  return (
    <SearchDropdown
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...dropdownProps}
      headerTitle={headerTitle ?? <Trans id="dropdown-search-inbox-title">Inbox</Trans>}
      headerRightIcon={headerRightIcon}
      data={filteredData}
      query={query}
      onQueryChange={setQuery}
      renderRow={(row) => {
        switch (row.type) {
          case SearchInboxDropdownItemsEnum.NONE:
            return renderNoneOption(selectedInboxIds.length === 0, onDelete);

          case SearchInboxDropdownItemsEnum.INBOX:
            return renderInbox(row.value, selectedInboxIds, isMultiSelect, onChange);

          case SearchInboxDropdownItemsEnum.CREATE_INBOX_HEADER:
          case SearchInboxDropdownItemsEnum.INBOX_HEADER:
            return renderHeader(row.type);

          case SearchInboxDropdownItemsEnum.CREATE_INBOX: {
            if (!onCreateInbox) {
              return null;
            }

            return renderCreateInbox(onCreateInbox);
          }

          case SearchInboxDropdownItemsEnum.LOADER:
            return <DropdownItemLoader />;

          default:
            throw new UnreachableError(row);
        }
      }}
      onScrollNearBottom={onLoadMore}
      footer={footer}
    />
  );
};

/*
 * Helpers.
 */

function buildDropdownItems(
  inboxes: ReadonlyArray<InboxModel>,
  shouldShowDeleteOption: boolean,
  shouldShowCreateInbox: boolean,
  asyncMoreStatus: AsyncMoreStatusesEnum | undefined,
): ReadonlyArray<SearchInboxDropdownRowTypedValue> {
  const inboxItems = _.compact([
    shouldShowDeleteOption ? dropdownNoneOption : undefined,
    ...inboxes.map(buildDropdownInbox),
    asyncMoreStatus !== undefined && asyncMoreStatus !== AsyncMoreStatusesEnum.ALL_LOADED
      ? dropdownLoader
      : undefined,
  ]);

  if (shouldShowCreateInbox) {
    return [dropdownCreateInboxHeader, dropdownCreateInbox, inboxHeader, ...inboxItems];
  }

  return inboxItems;
}

const dropdownCreateInboxHeader: SearchInboxDropdownRowTypedValue = {
  type: SearchInboxDropdownItemsEnum.CREATE_INBOX_HEADER,
  value: undefined,
};

const dropdownCreateInbox: SearchInboxDropdownRowTypedValue = {
  type: SearchInboxDropdownItemsEnum.CREATE_INBOX,
  value: undefined,
};

const inboxHeader: SearchInboxDropdownRowTypedValue = {
  type: SearchInboxDropdownItemsEnum.INBOX_HEADER,
  value: undefined,
};

const dropdownLoader: SearchInboxDropdownRowTypedValue = {
  type: SearchInboxDropdownItemsEnum.LOADER,
  value: undefined,
};

function buildDropdownInbox(value: InboxModel): SearchInboxDropdownRowTypedValue {
  return {
    type: SearchInboxDropdownItemsEnum.INBOX,
    value,
  };
}

const dropdownNoneOption: SearchInboxDropdownRowTypedValue = {
  type: SearchInboxDropdownItemsEnum.NONE,
  value: undefined,
};

function renderHeader(
  type: SearchInboxDropdownItemsEnum.CREATE_INBOX_HEADER | SearchInboxDropdownItemsEnum.INBOX_HEADER,
) {
  const renderName = () => {
    switch (type) {
      case SearchInboxDropdownItemsEnum.CREATE_INBOX_HEADER:
        return <Trans id="search-inbox-dropdown-create-inbox-header">Create</Trans>;

      case SearchInboxDropdownItemsEnum.INBOX_HEADER:
        return <Trans id="search-inbox-dropdown-inbox-header">All inboxes</Trans>;

      default:
        throw new UnreachableError(type);
    }
  };

  return <DropdownCategory>{renderName()}</DropdownCategory>;
}

function renderInbox(
  inbox: InboxModel,
  selectedInboxIds: ReadonlyArray<number>,
  isMultiSelect: boolean | undefined,
  onSelect: (model: InboxModel) => void,
) {
  const isChecked = selectedInboxIds.includes(inbox.id);

  return (
    <DropdownItem
      isChecked={isMultiSelect ? undefined : isChecked}
      onSelect={(event) => {
        if (isMultiSelect) {
          // Don't close the dropdown after (de)selecting an inbox.
          event.preventDefault();
        }
        onSelect(inbox);
      }}
      renderRightContent={isMultiSelect ? () => <DropdownItemCheckbox isChecked={isChecked} /> : undefined}
    >
      <DropdownItemInboxDot inbox={inbox} />
      <DropdownItemContent>{inbox.name}</DropdownItemContent>
    </DropdownItem>
  );
}

function renderNoneOption(isChecked: boolean, onSelect: (() => void) | undefined) {
  return (
    <DropdownItem
      onSelect={() => {
        if (onSelect) {
          onSelect();
        }
      }}
      isChecked={isChecked}
    >
      <DropdownItemIcon iconName="noneSmall" iconSize={16} label={null} />
      <DropdownItemContent>
        <Trans id="search-inbox-dropdown-none">None</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
}

function renderCreateInbox(onCreateInbox: () => void) {
  return (
    <DropdownItemCreate onCreate={onCreateInbox}>
      <Trans id="search-inbox-dropdown-create-inbox">New inbox</Trans>
    </DropdownItemCreate>
  );
}

const filterRows = memoize(20)((rows: ReadonlyArray<SearchInboxDropdownRowTypedValue>, query: string) => {
  if (!query) {
    return rows;
  }

  return rows.filter(
    (t) => t.type === SearchInboxDropdownItemsEnum.INBOX && doesInboxMatchQuery(t.value, query),
  );
});
